/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { ItemFrame } from "../../../shared/contracts";
import { EventProxy } from "../../core/event-proxy";
import type { IHittable } from "../../core/ihittable";
import type { IMovable } from "../../core/imovable";
import { Item } from "../../core/item";
import type { Player } from "../../core/player";
import type { HitCircle } from "../../physics/hit-circle";
import type { HitObject } from "../../physics/hit-object";
import type { Table } from "../../table-model";
import type { SpinnerData } from "./spinner-data";
import { SpinnerHit } from "./spinner-hit";
import { SpinnerHitGenerator } from "./spinner-hit-generator";
import type { SpinnerMover } from "./spinner-mover";
import { SpinnerState } from "./spinner-state";

/**
 * A spinner. A ball that rolls under the plate makes it turn. Each turn fires a spin event.
 */
export class Spinner extends Item<SpinnerData> implements IMovable, IHittable {
  private readonly state: SpinnerState;
  private readonly hitGenerator: SpinnerHitGenerator;
  private hit?: SpinnerHit;
  private hitCircles: HitCircle[] = [];

  public constructor(data: SpinnerData) {
    super(data);
    this.state = new SpinnerState(this.data.getName(), 0, data.showBracket, data.isVisible);
    this.hitGenerator = new SpinnerHitGenerator(data);
  }

  public isCollidable(): boolean {
    return true;
  }

  public setupPlayer(player: Player, table: Table): void {
    const height = table.getSurfaceHeight(
      this.data.szSurface,
      this.data.center.x,
      this.data.center.y,
    );
    this.events = new EventProxy(this, "spinner", player.getEventSink());
    this.hit = new SpinnerHit(this.data, this.state, this.events, height);
    this.hitCircles = this.hitGenerator.getHitShapes(this.state, height);
  }

  public getMover(): SpinnerMover {
    return this.hit!.getMoverObject();
  }

  public getHitShapes(): HitObject[] {
    return [this.hit!, ...this.hitCircles];
  }

  public getState(): SpinnerState {
    return this.state;
  }

  public toFrame(): ItemFrame {
    return this.state.toFrame();
  }
}
